import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShoppingCart, MapPin, Search, Menu, X, ChevronRight, Percent } from "lucide-react";
import { useStore } from "@/lib/store";
import api from "@/lib/api";

export default function Header() {
  const { user, logout, cartCount } = useStore();
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [categories, setCategories] = useState([]);
  const [allOpen, setAllOpen] = useState(false);
  const allRef = useRef(null);
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const close = () => setMenuOpen(false);
  const MobileLink = ({ to, children, testid }) => (
    <Link to={to} onClick={close} {...(testid ? { "data-testid": testid } : {})}
      className="block px-4 py-3 text-sm font-semibold text-slate-700 hover:text-primary hover:bg-primary/5 border-b border-slate-50">{children}</Link>
  );

  useEffect(() => { api.get("/meta").then((r) => setCategories(r.data.categories || [])).catch(() => {}); }, []);
  useEffect(() => {
    const onClick = (e) => { if (allRef.current && !allRef.current.contains(e.target)) setAllOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const submitSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (q.trim()) params.set("q", q.trim());
    if (cat) params.set("category", cat);
    navigate(`/buyer?${params.toString()}`);
  };

  const linkBase = "rounded-lg px-2.5 py-1.5 text-sm font-semibold text-slate-600 hover:text-primary hover:bg-primary/5 transition-colors";

  return (
    <header className="sticky top-0 z-50" data-testid="app-header">
      {/* Row 1 */}
      <div className="bg-white/90 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 h-16 flex items-center gap-2 sm:gap-4">
          <button onClick={() => setMenuOpen(true)} data-testid="mobile-menu-btn" aria-label="Open menu"
            className="md:hidden p-2 -ml-1 text-slate-700 rounded-lg hover:bg-slate-100">
            <Menu className="h-6 w-6" />
          </button>
          <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="header-logo">
            <img src="/logo-mx.png" alt="MaterialXpress" className="h-9 w-9 rounded-lg" />
            <span className="hidden sm:block font-display font-extrabold text-lg tracking-tight leading-none text-slate-900">
              Material<span className="text-primary">Xpress</span>
            </span>
          </Link>

          <Link to="/buyer" className="hidden lg:flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-slate-600 hover:bg-slate-50" data-testid="zone-status-pill">
            <MapPin className="h-4 w-4 text-primary" />
            <span className="leading-tight">
              <span className="block text-[11px] text-slate-400">Deliver to</span>
              <span className="block text-xs font-bold text-slate-800 -mt-0.5">Bengaluru</span>
            </span>
          </Link>

          <form onSubmit={submitSearch} className="flex flex-1 min-w-0 rounded-xl overflow-hidden border border-slate-300 bg-white focus-within:ring-2 focus-within:ring-primary/40 focus-within:border-primary transition">
            <select value={cat} onChange={(e) => setCat(e.target.value)} data-testid="header-category-select"
              className="hidden sm:block max-w-[140px] bg-slate-50 text-slate-600 text-xs font-semibold px-2 border-r border-slate-200 focus:outline-none">
              <option value="">All</option>
              {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <input data-testid="header-search-input" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search plywood, laminates, hardware…"
              className="flex-1 min-w-0 bg-white px-3 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none" />
            <button type="submit" data-testid="header-search-btn" className="px-3 sm:px-4 flex items-center justify-center bg-primary hover:bg-primary/90 transition">
              <Search className="h-5 w-5 text-white" />
            </button>
          </form>

          <nav className="flex items-center gap-1 sm:gap-2 shrink-0">
            {user ? (
              <div className="hidden sm:block leading-tight rounded-lg px-2 py-1 hover:bg-slate-50">
                <span className="block text-[11px] text-slate-400">Hello,</span>
                <span className="block text-xs font-bold text-slate-800 max-w-28 truncate" data-testid="header-user-name">{user.name}</span>
              </div>
            ) : (
              <Link to="/auth" data-testid="header-login-btn" className="leading-tight rounded-lg px-2 py-1 hover:bg-slate-50">
                <span className="block text-[11px] text-slate-400">Hello, sign in</span>
                <span className="block text-xs font-bold text-slate-800">Account</span>
              </Link>
            )}

            {user && (user.role === "buyer" || user.role === "admin") && (
              <Link to="/orders" data-testid="nav-orders-link" className="hidden md:block leading-tight rounded-lg px-2 py-1 hover:bg-slate-50">
                <span className="block text-[11px] text-slate-400">Returns</span>
                <span className="block text-xs font-bold text-slate-800">&amp; Orders</span>
              </Link>
            )}

            <Link to="/cart" data-testid="nav-cart-btn" className="relative flex items-end gap-1 rounded-lg px-2 py-1.5 hover:bg-slate-50">
              <div className="relative">
                <ShoppingCart className="h-6 w-6 text-slate-700" />
                {cartCount > 0 && (
                  <span data-testid="cart-count-badge" className="absolute -top-2 -right-2 flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold text-white">{cartCount}</span>
                )}
              </div>
              <span className="hidden sm:block text-xs font-bold text-slate-800">Cart</span>
            </Link>

            {user && (
              <button onClick={logout} data-testid="header-logout-btn" className="hidden sm:inline-flex items-center rounded-lg px-2 py-1 text-[11px] font-semibold text-slate-500 hover:text-primary hover:bg-primary/5">
                Sign out
              </button>
            )}
          </nav>
        </div>
      </div>

      {/* Row 2 */}
      <div className="hidden md:block bg-slate-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 h-11 flex items-center gap-1 text-sm">
          <div className="relative" ref={allRef}>
            <button onClick={() => setAllOpen((o) => !o)} data-testid="nav-all-btn"
              className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 font-bold text-slate-700 hover:text-primary hover:bg-primary/5">
              {allOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />} All
            </button>
            {allOpen && (
              <div className="absolute left-0 top-full mt-1 w-64 rounded-2xl bg-white text-slate-800 shadow-xl ring-1 ring-black/5 py-2 z-50 max-h-[70vh] overflow-y-auto" data-testid="nav-all-menu">
                <p className="px-4 py-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-400">Shop by category</p>
                {categories.map((c) => (
                  <Link key={c.id} to={`/buyer?category=${c.id}`} onClick={() => setAllOpen(false)}
                    className="flex items-center justify-between px-4 py-2 text-sm hover:bg-primary/5 hover:text-primary">
                    {c.name} <ChevronRight className="h-3.5 w-3.5 text-slate-300" />
                  </Link>
                ))}
              </div>
            )}
          </div>
          <Link to="/buyer" data-testid="nav-shop-link" className={linkBase}>Shop</Link>
          <Link to="/tools" data-testid="nav-tools-link" className={linkBase}>Tools</Link>
          <Link to="/tools?tab=floorplan" className={`hidden sm:inline-flex ${linkBase}`}>Floor Plan → BOQ</Link>
          {user && user.role === "buyer" && (
            <Link to="/lists" data-testid="nav-lists-link" className={linkBase}>Saved Lists</Link>
          )}
          {user && user.role === "vendor" && (
            <Link to="/vendor" data-testid="nav-vendor-link" className={linkBase}>Vendor Portal</Link>
          )}
          {user && user.role === "admin" && (
            <Link to="/admin" data-testid="nav-admin-link" className={linkBase}>Admin Hub</Link>
          )}
          <span className="ml-auto hidden md:inline-flex items-center gap-1.5 text-xs font-bold text-primary">
            <Percent className="h-3.5 w-3.5" /> Flat 30% off laminates
          </span>
        </div>
      </div>

      {/* Mobile slide-out menu */}
      {menuOpen && (
        <div className="fixed inset-0 z-[60] md:hidden" data-testid="mobile-menu-overlay">
          <div className="absolute inset-0 bg-black/40 animate-in fade-in" onClick={close} />
          <div className="absolute left-0 top-0 h-full w-72 max-w-[85vw] bg-white shadow-2xl flex flex-col animate-in slide-in-from-left duration-200" data-testid="mobile-menu-panel">
            <div className="flex items-center justify-between px-4 h-16 border-b border-slate-200 shrink-0">
              <Link to="/" onClick={close} className="flex items-center gap-2">
                <img src="/logo-mx.png" alt="MaterialXpress" className="h-8 w-8 rounded-lg" />
                <span className="font-display font-extrabold text-lg text-slate-900">Material<span className="text-primary">Xpress</span></span>
              </Link>
              <button onClick={close} data-testid="mobile-menu-close" aria-label="Close menu" className="p-2 text-slate-500 hover:text-slate-900"><X className="h-5 w-5" /></button>
            </div>
            <div className="flex-1 overflow-y-auto py-2">
              <div className="px-4 py-3 border-b border-slate-100">
                {user ? (
                  <p className="text-sm text-slate-600">Hello, <span className="font-bold text-slate-900">{user.name}</span></p>
                ) : (
                  <Link to="/auth" onClick={close} data-testid="mobile-login-link" className="btn-brand w-full">Sign in</Link>
                )}
              </div>
              <MobileLink to="/buyer" testid="mobile-nav-shop">Shop</MobileLink>
              <MobileLink to="/tools" testid="mobile-nav-tools">Tools</MobileLink>
              <MobileLink to="/tools?tab=floorplan">Floor Plan → BOQ</MobileLink>
              <MobileLink to="/cart" testid="mobile-nav-cart">Cart{cartCount > 0 ? ` (${cartCount})` : ""}</MobileLink>
              {user && (user.role === "buyer" || user.role === "admin") && <MobileLink to="/orders">Orders</MobileLink>}
              {user && user.role === "buyer" && <MobileLink to="/lists">Saved Lists</MobileLink>}
              {user && user.role === "vendor" && <MobileLink to="/vendor">Vendor Portal</MobileLink>}
              {user && user.role === "admin" && <MobileLink to="/admin">Admin Hub</MobileLink>}
              <p className="px-4 pt-4 pb-1 text-[11px] font-bold uppercase tracking-wider text-slate-400">Categories</p>
              {categories.map((c) => <MobileLink key={c.id} to={`/buyer?category=${c.id}`}>{c.name}</MobileLink>)}
              {user && (
                <button onClick={() => { close(); logout(); }} data-testid="mobile-logout-btn"
                  className="w-full text-left px-4 py-3 mt-2 text-sm font-semibold text-slate-500 hover:text-primary hover:bg-primary/5 border-t border-slate-100">Sign out</button>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
